// aiExtendContext — bygg AI-kontext för ai-workflow-assistant edge
// function. Pure helper; callern är AiAssistantDialog.performSend (I-002).
//
// Fas 2: stödjer både single-sheet (legacy) och multi-sheet snapshot.
// Edge-funktionen detekterar shape via närvaron av `sheets`-fältet och
// renderar guard-prompten därefter (sheet-by-sheet + cross-sheet info).
//
// Bug-B fix: tidigare skickades snapshot ENBART vid intent='extend' — men
// alla intents som rör befintligt canvas-content (modify_in_place,
// refactor, explain) behöver också snapshoten för att AI:n överhuvudtaget
// ska kunna se vad som finns. Edge-funktionen aktiverar EXTEND-guarden
// bara vid intent='extend' och behandlar snapshot som ren reference-
// context för andra intents (analys / förklaring / ändring i place).

import type { Intent } from '@/lib/intentTypes';
import type { MultiSheetWorkflowState, WorkflowState } from '@/types/workflow';
import { isMultiSheetState, wrapSingleSheet } from '@/types/workflow';

export type ExtendSnapshot = MultiSheetWorkflowState | WorkflowState;

/**
 * Intents som behöver canvas-snapshot för att AI:n ska kunna agera
 * meningsfullt:
 *   - extend          → bygg vidare (EXTEND-guard aktiveras)
 *   - modify_in_place → ändra befintliga element
 *   - refactor        → "trassla ut", omstrukturera, döp om grupper
 *   - explain         → förklara vad flödet gör
 *
 * Intents som INTE behöver snapshot:
 *   - build_new       → skapa från noll (snapshot bara förvirrar)
 *   - revert          → klient-side time-travel, ingen LLM
 *   - meta.*          → moderation/history, inte canvas
 */
const INTENTS_NEEDING_SNAPSHOT: readonly Intent[] = [
  'extend', 'modify_in_place', 'refactor', 'explain',
] as const;

export function intentNeedsSnapshot(intent: Intent): boolean {
  return INTENTS_NEEDING_SNAPSHOT.includes(intent);
}

/**
 * Returnerar fetch-body-fragmentet `{ intent, previous_version_content }`
 * när AI:n behöver canvas-context, annars `undefined`. Edge-function
 * läser båda fälten — vid intent='extend' aktiveras EXTEND-guarden
 * (Batch DDD), vid andra canvas-relevanta intents används snapshoten
 * bara som reference för analys/förklaring.
 *
 * `previous_version_content` är alltid en `MultiSheetWorkflowState` så
 * edge-funktionen kan rendera ALL sheets + cross-sheet-connectors. Legacy
 * single-sheet `WorkflowState` auto-wrappas via `wrapSingleSheet`.
 */
export function buildExtendContext(
  intent: Intent,
  snapshot: ExtendSnapshot | undefined,
): { intent: Intent; previous_version_content: MultiSheetWorkflowState } | undefined {
  if (!intentNeedsSnapshot(intent)) return undefined;
  if (!snapshot) return undefined;
  const multi = isMultiSheetState(snapshot) ? snapshot : wrapSingleSheet(snapshot);
  return { intent, previous_version_content: multi };
}
